import { buffers, EventChannel, eventChannel } from 'redux-saga';

export type ArrayUnknown = Array<unknown>;

export type EmitterOnOff<T extends ArrayUnknown> = {
    on: (event: string, listener: (...args: T) => void) => void;
    off: (event: string, listener: (...args: T) => void) => void;
};

type EmitterAddRemove<T extends ArrayUnknown> = {
    addEventListener: (event: string, listener: (...args: T) => void) => void;
    removeEventListener: (event: string, listener: (...args: T) => void) => void;
};

type Emitter<T extends ArrayUnknown> = EmitterOnOff<T> | EmitterAddRemove<T>;

const isEmitterOnOff = <T extends ArrayUnknown>(emitter: Emitter<T>): emitter is EmitterOnOff<T> => {
    return 'on' in emitter && typeof emitter.on === 'function' && 'off' in emitter && typeof emitter.off === 'function';
};

export const emitterEventChannelFactory = <T extends ArrayUnknown = []>(
    emitter: Emitter<T> | null,
    eventName: string,
    buffer = buffers.expanding<T>()
): EventChannel<T> => {
    return eventChannel<T>((emit) => {
        const listener = (...args: T) => {
            emit(args);
        };

        if (!emitter) {
            return () => null;
        }

        if (isEmitterOnOff(emitter)) {
            emitter.on(eventName, listener);
            return () => emitter.off(eventName, listener);
        } else {
            emitter.addEventListener(eventName, listener);
            return () => emitter.removeEventListener(eventName, listener);
        }
    }, buffer);
};
